import { hexLen, hexToWorld, ROW, type Hex } from '../hex.ts';
import { idiv, imax, imin } from '../math/fixed.ts';
import { deriveSeed, rngInt, seedRng } from '../math/rng.ts';

/**
 * The cleric's last road, chapter 2 (docs/sendas/clerigo.md, «La pasarela»): its grounds, the same from the seed in the
 * sim and the drawing, integers only. Up is −y (the top of the screen); the cliff is always to the west (−x), the fog
 * to the east.
 *
 * - `ridgeLayout` (act 1, the cliff path): a narrow way up the cliff's face, a hex or two wide, winding a little; the
 *   rock to the west, the drop to the fog to the east. Five chapels cut into the rock along it (the first at its foot,
 *   where it starts; the stretcher waits in each), and across the fog the slingers' ledge, out of reach.
 * - `walkLayout` (act 2, the walkway): four spans of planks over the fog, each four planks long, climbing in a zigzag
 *   along the cliff (north-east, north-west, …) between five landings of rock; on each of the first four landings a post
 *   with its mirror, at the start of its span; on the last, the chapel. The moon comes in by one gap in the fog by the
 *   first landing.
 * - `bridgeLayout` (act 3, the test): a straight bridge of planks hung from chains, from the near chapel at the foot to
 *   the far one at the top, a plank a row (one cell or two across); the mangonel on the far chapel.
 */

export const RIDGE_RADIUS = 12;
export const WALK_RADIUS = 11;
export const BRIDGE_RADIUS = 10;

const allCells = (R: number): Hex[] => {
  const out: Hex[] = [];
  for (let r = -R; r <= R; r++) for (let q = -R; q <= R; q++) if (hexLen(q, r) <= R) out.push({ q, r });
  return out;
};
const sortHex = (a: Hex, b: Hex): number => a.r - b.r || a.q - b.q;
const key = (q: number, r: number): number => (q + 64) * 256 + (r + 64);

/* ---------- the cliff path ---------- */

export interface RidgeLayout {
  radius: number;
  rocks: Hex[];
  /** The path's cells (the chapels' niches among them). */
  path: Hex[];
  /** The stretcher's way, row by row from the foot to the walkway's head. */
  way: { x: number; y: number }[];
  /** The chapels: where each stands (in its niche, the rock's side), and the way's point the stretcher waits at. */
  chapels: { x: number; y: number; at: number }[];
  /** Where gargoyles come off the rock, each by a point of the way. */
  dens: { x: number; y: number; at: number }[];
  /** The slingers' ledge across the fog (its x) and the muleteer's spot in the second chapel. */
  ledge: number;
  arriero: { x: number; y: number };
  start: { x: number; y: number };
}

export const ridgeLayout = (seed: number): RidgeLayout => {
  const R = RIDGE_RADIUS;
  const rng = seedRng(deriveSeed(seed, 0xc2a1));
  // The way's middle on each row, from the foot (r = R − 1) to the top: it winds a half hex every other row or so, near
  // the cliff (x from −3000 to −500).
  const mid = new Map<number, number>();
  let x = -1500;
  let dir = rngInt(rng, 2) === 0 ? -1 : 1;
  for (let r = R - 1; r >= -(R - 1); r--) {
    mid.set(r, x);
    if (rngInt(rng, 10) < 4) {
      if (rngInt(rng, 10) < 3) dir = -dir;
      x = imax(-3000, imin(-500, x + dir * 500));
      if (x === -3000 || x === -500) dir = -dir;
    }
  }
  const rows: number[] = [];
  for (let r = R - 1; r >= -(R - 1); r--) rows.push(r);
  const way = rows.map((r) => ({ x: mid.get(r) as number, y: r * ROW }));
  // The chapels: at the foot and every four or five rows, the last one two rows short of the top.
  const at = [0, 5, 10, 15, 19];
  const chapels = at.map((i) => {
    const p = way[i] as { x: number; y: number };
    return { x: p.x - 1100, y: p.y, at: i };
  });
  const path: Hex[] = [];
  const rocks: Hex[] = [];
  for (const h of allCells(R)) {
    const p = hexToWorld(h.q, h.r);
    const m = mid.get(h.r);
    const foot = h.r >= R - 1;
    const mm = m ?? mid.get(R - 1) ?? 0;
    const near = Math.abs(p.x - mm) <= (foot ? 1600 : 700);
    // A chapel's niche: a hex into the rock on its row and the row above.
    const niche = chapels.some((c) => Math.abs(h.r * ROW - c.y) <= ROW && p.x >= c.x - 700 && p.x < mm - 300);
    if ((m !== undefined || foot) && (near || niche)) path.push(h);
    else rocks.push(h);
  }
  const dens: { x: number; y: number; at: number }[] = [];
  for (let i = 2; i < way.length; i += 3) {
    const p = way[i] as { x: number; y: number };
    dens.push({ x: p.x - 2600, y: p.y, at: i });
  }
  rocks.sort(sortHex);
  path.sort(sortHex);
  const c2 = chapels[2] as { x: number; y: number };
  const foot = way[0] as { x: number; y: number };
  return { radius: R, rocks, path, way, chapels, dens, ledge: 5200, arriero: { x: c2.x - 200, y: c2.y - 500 }, start: { x: foot.x + 300, y: foot.y + 500 } };
};

/* ---------- the walkway ---------- */

/** A mirror's position on its post: down (lights its span, the beam ends there), on (to the next post), back (to the moon). */
export const WALK_DOWN = 0;
export const WALK_ON = 1;
export const WALK_BACK = 2;

export interface WalkLayout {
  radius: number;
  rocks: Hex[];
  /** The landings (rock), from the foot: four with a post at the start of a span, the last with the chapel. */
  landings: Hex[];
  /** Each span's planks, from its landing up. */
  spans: Hex[][];
  /** Each post: its cell (the landing's), where its mirror stands (a little off the way, the cliff's side). */
  posts: { q: number; r: number; x: number; y: number }[];
  /** The stretcher's way: landing, plank, plank, …, landing. Each point's span (−1 a landing). */
  way: { x: number; y: number }[];
  waySpan: number[];
  /** Where the moon comes in (over the first landing, out over the fog). */
  moon: { x: number; y: number };
  /** Where the mirrors start (the seed's). */
  start0: number[];
  start: { x: number; y: number };
}

/** Which span the moon lies on with the mirrors as `turns` (−1: none): the first post down, all before it on. */
export const walkLit = (turns: readonly number[]): number => {
  for (let i = 0; i < turns.length; i++) {
    const t = turns[i];
    if (t === WALK_DOWN) return i;
    if (t !== WALK_ON) return -1;
  }
  return -1;
};

/** How far the beam goes: the posts it reaches (from the first), and whether the last of them sends it back. */
export const walkReach = (turns: readonly number[]): { posts: number; back: boolean } => {
  for (let i = 0; i < turns.length; i++) {
    const t = turns[i];
    if (t === WALK_DOWN) return { posts: i + 1, back: false };
    if (t === WALK_BACK) return { posts: i + 1, back: true };
  }
  return { posts: turns.length, back: false };
};

export const walkLayout = (seed: number): WalkLayout => {
  const R = WALK_RADIUS;
  const rng = seedRng(deriveSeed(seed, 0xc2b2));
  // From the foot landing up: north-east (q + 1, r − 1), then north-west (q, r − 1), and again.
  const SPANS = 4;
  const PLANKS = 4;
  const dirs: Hex[] = [
    { q: 1, r: -1 },
    { q: 0, r: -1 },
    { q: 1, r: -1 },
    { q: 0, r: -1 },
  ];
  let cur: Hex = { q: -4, r: 10 };
  const landings: Hex[] = [{ ...cur }];
  const spans: Hex[][] = [];
  for (let i = 0; i < SPANS; i++) {
    const d = dirs[i] as Hex;
    const sp: Hex[] = [];
    for (let k = 1; k <= PLANKS; k++) sp.push({ q: cur.q + d.q * k, r: cur.r + d.r * k });
    spans.push(sp);
    cur = { q: cur.q + d.q * (PLANKS + 1), r: cur.r + d.r * (PLANKS + 1) };
    landings.push({ ...cur });
  }
  const walk = new Set<number>();
  for (const l of landings) walk.add(key(l.q, l.r));
  for (const sp of spans) for (const c of sp) walk.add(key(c.q, c.r));
  // Each landing a little wider on the cliff's side (west): the post stands there, and the foot and the chapel more.
  const extra: Hex[] = [];
  landings.forEach((l, i) => {
    extra.push({ q: l.q - 1, r: l.r });
    if (i === 0 || i === landings.length - 1) extra.push({ q: l.q - 1, r: l.r + 1 }, { q: l.q, r: l.r + 1 }, { q: l.q - 2, r: l.r + 1 });
    if (i === landings.length - 1) extra.push({ q: l.q - 1, r: l.r - 1 }, { q: l.q, r: l.r - 1 });
  });
  for (const e of extra) if (hexLen(e.q, e.r) <= R) walk.add(key(e.q, e.r));
  const rocks = allCells(R).filter((h) => !walk.has(key(h.q, h.r)));
  rocks.sort(sortHex);
  const posts = landings.slice(0, SPANS).map((l) => {
    const p = hexToWorld(l.q, l.r);
    return { q: l.q, r: l.r, x: p.x - 520, y: p.y };
  });
  const way: { x: number; y: number }[] = [];
  const waySpan: number[] = [];
  for (let i = 0; i <= SPANS; i++) {
    way.push(hexToWorld((landings[i] as Hex).q, (landings[i] as Hex).r));
    waySpan.push(-1);
    if (i < SPANS)
      for (const c of spans[i] as Hex[]) {
        way.push(hexToWorld(c.q, c.r));
        waySpan.push(i);
      }
  }
  // The mirrors as the seed leaves them: the first never down (its span starts inked); now the next one down already,
  // now one further on down (it takes the light if it isn't turned first).
  const start0 = [1 + rngInt(rng, 2)];
  for (let i = 1; i < SPANS; i++) start0.push(rngInt(rng, 3));
  if (rngInt(rng, 2) === 0) start0[2 + rngInt(rng, 2)] = WALK_DOWN;
  const l0 = hexToWorld((landings[0] as Hex).q, (landings[0] as Hex).r);
  return { radius: R, rocks, landings, spans, posts, way, waySpan, moon: { x: l0.x + 3400, y: l0.y - 1800 }, start0, start: { x: l0.x - 700, y: l0.y + 700 } };
};

/* ---------- the chapels' bridge ---------- */

export interface BridgeLayout {
  radius: number;
  rocks: Hex[];
  /** The near chapel's cells (the foot) and the far one's (the top). */
  near: Hex[];
  far: Hex[];
  /** Each plank: its row (r) and its cells (one or two across). From the foot up. */
  planks: { r: number; cells: Hex[] }[];
  way: { x: number; y: number }[];
  /** Where the limpets climb out of the fog, each by a point of the way. */
  dens: { x: number; y: number; at: number }[];
  mangonel: { x: number; y: number };
  start: { x: number; y: number };
}

export const BRIDGE_ROWS = 7;

export const bridgeLayout = (seed: number): BridgeLayout => {
  void seed;
  const R = BRIDGE_RADIUS;
  const near: Hex[] = [];
  const far: Hex[] = [];
  const planks: { r: number; cells: Hex[] }[] = [];
  for (let r = BRIDGE_ROWS; r >= -BRIDGE_ROWS; r--) planks.push({ r, cells: [] });
  const rocks: Hex[] = [];
  for (const h of allCells(R)) {
    const p = hexToWorld(h.q, h.r);
    if (h.r > BRIDGE_ROWS && Math.abs(p.x) <= 2100) near.push(h);
    else if (h.r < -BRIDGE_ROWS && Math.abs(p.x) <= 2100) far.push(h);
    else if (Math.abs(h.r) <= BRIDGE_ROWS && Math.abs(p.x) <= 600) planks.find((k) => k.r === h.r)?.cells.push(h);
    else rocks.push(h);
  }
  rocks.sort(sortHex);
  near.sort(sortHex);
  far.sort(sortHex);
  for (const k of planks) k.cells.sort(sortHex);
  const way: { x: number; y: number }[] = [];
  for (let r = BRIDGE_ROWS + 1; r >= -(BRIDGE_ROWS + 1); r--) way.push({ x: 0, y: r * ROW });
  const dens: { x: number; y: number; at: number }[] = [];
  for (let i = 2; i < way.length - 2; i += 2) {
    const p = way[i] as { x: number; y: number };
    dens.push({ x: (i & 2 ? -1 : 1) * 2300, y: p.y, at: i });
  }
  return { radius: R, rocks, near, far, planks, way, dens, mangonel: { x: 0, y: -(BRIDGE_ROWS + 2) * ROW - 300 }, start: { x: 700, y: (BRIDGE_ROWS + 2) * ROW } };
};

/** The plank of the bridge under a point (its index from the foot), or −1 off the planks (a chapel). */
export const plankAt = (y: number): number => {
  const r = idiv(y >= 0 ? y + 433 : y - 433, ROW);
  if (Math.abs(r) > BRIDGE_ROWS) return -1;
  return BRIDGE_ROWS - r;
};
